import { useRef, useState } from "react";
import { Box, Button, Menu, MenuButton, MenuItem, MenuList, Text } from "@chakra-ui/react";
import useClickOutside from "../../hooks/useClickOutside";
import useRect from "../../hooks/useRect";
import useWindowSize from "../../hooks/useWindowSize";
import { typedSxMap } from "../../utils/typedSxMap";
import { SelectProps } from "./types";
import Tooltip from "../Tooltip";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";

const styles = typedSxMap({
  inputButton: {
    alignItems: "center",
    borderRadius: "var(--border-radius-1)",
    display: "flex",
    justifyContent: "space-between",
    fontWeight: 400,
    width: "100%",
    padding: " calc(var(--m-xxs) / 2) var(--m-xxs)",
    position: "relative",
    height: "31px",
    color: "var(--color-text)",
    "&:active, &:focus, &:hover, &[data-active]": {
      boxShadow: "none",
      background: "none",
    },
  },
  menuList: {
    backgroundColor: "var(--color-input-background)",
    borderRadius: "var(--border-radius-2)",
    padding: "var(--m-xxxxs)",
    border: "1px solid var(--color-border)",
    outline: "none",
    display: "flex",
    flexDirection: "column",
    gap: "var(--m-xxxxs)",
    maxHeight: "50vh",
    overflowY: "auto",
    marginTop: "var(--m-xxxxs)",
    zIndex: 9999,
  },
  option: {
    padding: "var(--m-xxs) var(--m-xs)",
    borderRadius: "var(--border-radius-2)",
    backgroundColor: "var(--color-background-menu-item)",
    "&:active, &:focus, &:hover": {
      backgroundColor: "var(--color-background-menu-hover)",
    },
  },
  selected: {
    backgroundColor: "var(--color-background-menu-hover)",
    color: "var(--color-text)",
  },
  placeholder: {
    color: "var(--color-text-disabled)",
  },
  requiredMark: {
    color: "var(--color-error)",
    marginLeft: "var(--m-xxxs)",
  },
  disabledOption: {
    color: "var(--color-text-soft)",
  },
  optionIcon: {
    marginLeft: "var(--m-xxs)",
    marginBottom: "2px",
    verticalAlign: "middle",
  },
  wrapper: {
    backgroundColor: "var(--color-input-background)",
    borderRadius: "var(--border-radius-2)",
    padding: "calc(var(--m-xxs) / 2)",
    border: "1px solid var(--color-border)",
    fontWeight: 400,
    display: "flex",
    alignItems: "center",
    minWidth: "270px",
    position: "relative",
    outline: "1px solid transparent",
    "&:focus-within": {
      outlineColor: "var(--color-text)",
    },
    "& button": {
      background: "transparent",
    },
  },
  label: {
    marginBottom: "var(--m-xxs)",
  },
  icon: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    marginRight: "var(--m-xxs)",
    "& svg": {
      display: "block",
      width: "var(--m-xs)",
      height: "var(--m-xs)",
    },
  },
  menuItemIcon: {
    display: "inline-flex",
    alignItems: "center",
    marginRight: "var(--m-xxs)",
    "& svg": {
      display: "block",
      width: "var(--m-xs)",
      height: "var(--m-xs)",
    },
  },
});

export function Select({ options = {}, placeholder, disabled, label, ...props }: SelectProps) {
  const [buttonIcon, setButtonIcon] = useState(<IoChevronDown />);
  const [open, setOpen] = useState(false);

  const onChangeOption = (e: any) => {
    const value = e.target?.value || e.target.closest("button")?.value;
    props?.onChange && props?.onChange(value);
    onBlur();
  };

  const selectedKey = Object.keys(options).find((k) => options[k].value === props.value) || "";

  const [setRef, size] = useRect();

  const windowSize = useWindowSize();

  const top = size.y + size.height > windowSize[1] - 4 ? windowSize[1] - size.height - 4 : size.y + 4;

  const optionsPosition = {
    top: `${top}px`,
    left: `${size?.x}px`,
    width: `${size?.right - size?.left}px`,
  };

  const onBlur = () => {
    setOpen(false);
  };

  const ref = useRef(null);

  useClickOutside(ref, onBlur);

  return (
    <Box>
      {label && <Box sx={styles.label}>{label}</Box>}
      <Box sx={styles.wrapper}>
        <Menu
          onClose={() => {
            setButtonIcon(<IoChevronDown />);
          }}
          onOpen={() => setButtonIcon(<IoChevronUp />)}>
          <MenuButton disabled={disabled} isDisabled={disabled} ref={setRef} sx={styles.inputButton} as={Button} rightIcon={buttonIcon}>
            {selectedKey && options[selectedKey] ? (
              <>
                {options[selectedKey].icon && <Box sx={styles.icon}>{options[selectedKey].icon}</Box>}
                {options[selectedKey].name}
              </>
            ) : (
              placeholder
            )}
          </MenuButton>
          <MenuList sx={styles.menuList} style={optionsPosition}>
            {placeholder && (
              <MenuItem key={-1} sx={{ ...styles.option, ...styles.disabledOption }} onClick={onChangeOption} type="button">
                {placeholder}
              </MenuItem>
            )}
            {Object.keys(options).map((k) => {
              // Spread the option to a new object and handle the 'key' property separately
              const { icon, name, required, tooltip, value, ...restOptionProps } = options[k];
              // Remove 'key' from restOptionProps to avoid React warning
              const { key: _, ...cleanProps } = restOptionProps as any;

              return (
                <MenuItem
                  key={k}
                  type="button"
                  value={value}
                  onClick={onChangeOption}
                  sx={{ ...styles.option, ...(k === props.value ? styles.selected : undefined) }}
                  {...cleanProps}>
                  {icon && <Box sx={styles.menuItemIcon}>{icon}</Box>}
                  {name || k}
                  {required && <Text sx={styles.requiredMark}>*</Text>}
                  {tooltip && <Tooltip title={tooltip}></Tooltip>}
                </MenuItem>
              );
            })}
          </MenuList>
        </Menu>
      </Box>
    </Box>
  );
}
